import type { ProcessType } from 'shared/domain/process/types/model';
import type { IssueWorkflowOutDto } from 'shared/domain/workflow/types';
import { stableSort } from 'helpers/sort';
import { findById } from 'shared/utils/id/findById';

export function getWorkflowIdForProcess(
  processes: ProcessType[],
  processId: string | undefined
): string | undefined {
  if (processId === undefined) {
    return undefined;
  }
  const process = findById(processes, processId, 'getWorkflowIdForProcess');
  const raw = process?.workflowId;
  if (raw === undefined) {
    return undefined;
  }
  const trimmed = raw.trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * Workflow shown/edited for a process: server assignment if set, otherwise first workflow by name (no “none” in UI).
 */
export function resolveWorkflowIdForFieldVisibility(
  processes: ProcessType[],
  processId: string | undefined,
  workflows: IssueWorkflowOutDto[]
): string | undefined {
  const fromServer = getWorkflowIdForProcess(processes, processId);
  if (fromServer !== undefined) {
    return fromServer;
  }
  if (workflows.length === 0) {
    return undefined;
  }
  const sorted = stableSort([...workflows], (a, b) =>
    a.name.localeCompare(b.name)
  );
  return sorted[0]._id;
}

/** A workflow the user chose for a process and has not saved yet. */
export interface WorkflowChoice {
  processId: string;
  workflowId: string;
}

/** The workflow shown for a process: the user's unsaved choice for it, otherwise its assigned one. */
export function resolveSelectedWorkflowId(
  workflowChoice: WorkflowChoice | undefined,
  processes: ProcessType[],
  /** Undefined before the processes load. */
  processId: string | undefined,
  workflows: IssueWorkflowOutDto[]
): string | undefined {
  if (workflowChoice && workflowChoice.processId === processId) {
    return workflowChoice.workflowId;
  }
  return resolveWorkflowIdForFieldVisibility(processes, processId, workflows);
}

/** The process the user chose, or the first one; undefined before the processes load. */
export function findSelectedProcess(
  processes: ProcessType[],
  chosenProcessId: string | undefined
): ProcessType | undefined {
  const [firstProcess] = processes;
  return processes.find(({ _id }) => _id === chosenProcessId) ?? firstProcess;
}
